import { localGet, localSet, sessionGet, sessionSet, storageKey } from '../lib/storageKeys'
import type { UiStrings } from './types'
import de from './locales/de'
import en from './locales/en'
import es from './locales/es'
import fr from './locales/fr'
import ja from './locales/ja'
import ko from './locales/ko'
import ptBR from './locales/pt-BR'
import ru from './locales/ru'
import zhTW from './locales/zh-TW'

export type { UiStrings } from './types'

export type Locale =
  | 'en'
  | 'ko'
  | 'ja'
  | 'pt-BR'
  | 'es'
  | 'ru'
  | 'de'
  | 'fr'
  | 'zh-TW'

export const LOCALES: { id: Locale; label: string; short: string }[] = [
  { id: 'en', label: 'English', short: 'EN' },
  { id: 'ko', label: '한국어', short: 'KO' },
  { id: 'ja', label: '日本語', short: 'JA' },
  { id: 'pt-BR', label: 'Português (Brasil)', short: 'PT' },
  { id: 'es', label: 'Español', short: 'ES' },
  { id: 'ru', label: 'Русский', short: 'RU' },
  { id: 'de', label: 'Deutsch', short: 'DE' },
  { id: 'fr', label: 'Français', short: 'FR' },
  { id: 'zh-TW', label: '繁體中文', short: 'ZH' },
]

export const DEFAULT_LOCALE: Locale = 'en'
export const LOCALE_STORAGE_KEY = 'holdcard-ex:locale'
export const LOCALE_STORAGE_KEY_LEGACY = 'holecard-ex:locale'

const catalog: Record<Locale, UiStrings> = {
  en,
  ko,
  ja,
  'pt-BR': ptBR,
  es,
  ru,
  de,
  fr,
  'zh-TW': zhTW,
}

const LOCALE_IDS = new Set<string>(LOCALES.map((l) => l.id))

let currentLocale: Locale = DEFAULT_LOCALE

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && LOCALE_IDS.has(value)
}

/** First-visit locale from navigator (persisted locale always wins). */
export function detectBrowserLocale(): Locale {
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined
    if (!nav) return DEFAULT_LOCALE
    const candidates: string[] = []
    if (Array.isArray(nav.languages)) candidates.push(...nav.languages)
    if (nav.language) candidates.push(nav.language)
    for (const raw of candidates) {
      const lower = String(raw).toLowerCase().replace(/_/g, '-')
      if (lower.startsWith('ko')) return 'ko'
      if (lower.startsWith('ja')) return 'ja'
      if (lower === 'pt-br' || lower.startsWith('pt-br')) return 'pt-BR'
      if (lower.startsWith('pt')) return 'pt-BR'
      if (lower.startsWith('es')) return 'es'
      if (lower.startsWith('ru')) return 'ru'
      if (lower.startsWith('de')) return 'de'
      if (lower.startsWith('fr')) return 'fr'
      if (lower === 'zh-tw' || lower.startsWith('zh-tw') || lower === 'zh-hk' || lower.startsWith('zh-hk')) {
        return 'zh-TW'
      }
      // bare zh / zh-CN → en (do not prefer zh-CN)
      if (lower === 'zh' || lower.startsWith('zh-')) continue
    }
  } catch {
    // ignore
  }
  return DEFAULT_LOCALE
}

export function hasStoredLocale(): boolean {
  return getStoredLocale() != null
}

/** Persisted locale only (null if unset). Migrates legacy key on read. */
export function getStoredLocale(): Locale | null {
  try {
    const raw = localGet('locale')
    if (isLocale(raw)) return raw
  } catch {
    // private mode
  }
  return null
}

/** Path-based locale for crawlable shells (e.g. /ko). */
export function localeFromPathname(pathname = location.pathname): Locale | null {
  const p = pathname.replace(/\/+$/, '') || '/'
  if (p === '/ko') return 'ko'
  return null
}

/** Soft I2a: footer language pick made in this tab session (sessionStorage). */
const LOCALE_PICK_SUFFIX = 'locale-pick'

/** Footer pick from this session (null if none / storage unavailable). */
export function getSessionLocalePick(): Locale | null {
  try {
    const raw = sessionGet(LOCALE_PICK_SUFFIX)
    if (isLocale(raw)) return raw
  } catch {
    // private mode
  }
  return null
}

/**
 * Soft I2a: an explicit footer language pick. Saved to holdcard-ex:locale for
 * later visits and wins over the /ko path for the rest of this session.
 * Storage failures are ignored — the link still navigates as before.
 */
export function rememberFooterLocalePick(locale: Locale): void {
  sessionSet(LOCALE_PICK_SUFFIX, locale)
  localSet('locale', locale)
}

/** A newer pick (header select) supersedes the session footer pick. */
export function clearSessionLocalePick(): void {
  try {
    sessionStorage.removeItem(storageKey(LOCALE_PICK_SUFFIX))
  } catch {
    // private mode
  }
}

export function loadStoredLocale(): Locale {
  // Soft I2a priority: footer pick (this session) → /ko path → stored → browser.
  // The /ko path never persists — leaving /ko still respects holdcard-ex:locale.
  return getSessionLocalePick() ?? localeFromPathname() ?? getStoredLocale() ?? detectBrowserLocale()
}

export function setLocale(locale: Locale, opts?: { persist?: boolean }): void {
  currentLocale = locale
  if (opts?.persist === false) return
  localSet('locale', locale)
}

export function initLocale(locale?: Locale): Locale {
  currentLocale = locale ?? loadStoredLocale()
  return currentLocale
}

/** Current UI strings for the active locale. */
export function t(): UiStrings {
  return catalog[currentLocale]
}

export function getLocale(): Locale {
  return currentLocale
}
